import React from 'react';
import {useTranslation} from 'react-i18next';

import {Modal, ModalActions, ModalHeader, ModalSubmitButton, showModal} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {parseISO} from 'src/utils';

import {useGetConnectedTariffHistoryBySubjectIdQuery, useGetTariffHistoryQuery} from '../api';
import {TAvailableTariff, TConnectedTariff} from '../definitions';

const TariffHistoryModal = <T extends {id: number; version: number; activeFrom: Date | string}>({
  closeModal,
  tariffs,
  tariff,
}: {
  closeModal: TEmptyFunction;
  tariffs: T[];
  tariff: T;
}) => {
  const {t} = useTranslation();

  return (
    <Modal width="580px">
      <ModalHeader>{t('Tariffs:historyModal.title')}</ModalHeader>
      <Stack size="2">
        {tariffs.map((item) => (
          <Text secondary size="S" key={`${item.id}-${item.version}`}>
            {t('Tariffs:historyModal.changeDate', {date: parseISO(item.activeFrom, 'DATE_HM')})}
          </Text>
        ))}
        {t('Tariffs:historyModal.connectDate', {
          date: parseISO(tariff.activeFrom, 'DATE_HM'),
        })}
      </Stack>
      <ModalActions>
        <ModalSubmitButton onClick={closeModal}>{t('Common:button.understand')}</ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
};

TariffHistoryModal.displayName = 'TariffHistoryModal';

const AvailableTariffHistoryModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
}>(({closeModal, tariff}) => {
  const {data: tariffs = []} = useGetTariffHistoryQuery(tariff.id);

  return <TariffHistoryModal closeModal={closeModal} tariffs={tariffs} tariff={tariff} />;
});

AvailableTariffHistoryModal.displayName = 'AvailableTariffHistoryModal';

const ConnectedTariffHistoryModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TConnectedTariff;
}>(({closeModal, tariff}) => {
  const {data: tariffs = []} = useGetConnectedTariffHistoryBySubjectIdQuery(tariff.subjectId);

  return <TariffHistoryModal closeModal={closeModal} tariffs={tariffs} tariff={tariff} />;
});

ConnectedTariffHistoryModal.displayName = 'ConnectedTariffHistoryModal';

export const showAvailableTariffHistoryModal = (tariff: TAvailableTariff) =>
  showModal((closeModal) => (
    <AvailableTariffHistoryModal closeModal={closeModal} tariff={tariff} />
  ));

export const showConnectedTariffHistoryModal = (tariff: TConnectedTariff) =>
  showModal((closeModal) => (
    <ConnectedTariffHistoryModal closeModal={closeModal} tariff={tariff} />
  ));
